import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import api, { getForumStats, getTrendingForumPosts } from '../../utils/api';
import { MessageSquare, ThumbsUp, ThumbsDown, Clock, Search, Filter, Plus, User, Hash, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const CommunityForum = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedTag, setSelectedTag] = useState('');
    const [selectedSort, setSelectedSort] = useState('latest');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentUser, setCurrentUser] = useState(null);

    const categories = [
        { id: '', name: 'Latest' },
        { id: 'Google', name: 'Google' },
        { id: 'Amazon', name: 'Amazon' },
        { id: 'Meta', name: 'Meta' },
        { id: 'System Design', name: 'System Design' }
    ];

    const [trendingTopics, setTrendingTopics] = useState([]);
    const [topContributors, setTopContributors] = useState([]);
    const [trendingPosts, setTrendingPosts] = useState([]);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const res = await api.get('/auth/profile');
                setCurrentUser(res.data);
            } catch (err) {
                setCurrentUser(null);
            }
        };
        const fetchStats = async () => {
            try {
                const [stats, trending] = await Promise.all([
                    getForumStats(),
                    getTrendingForumPosts(3)
                ]);
                setTrendingTopics(stats.trending_topics || []);
                setTopContributors(stats.top_contributors || []);
                setTrendingPosts(trending || []);
            } catch (err) {
                console.error("Failed to fetch forum stats:", err);
            }
        };
        checkAuth();
        fetchStats();
        fetchPosts();
    }, [selectedCategory, selectedTag, selectedSort]);

    const fetchPosts = async () => {
        try {
            setLoading(true);
            let url = '/api/forum/posts?';
            if (selectedCategory) url += `category=${encodeURIComponent(selectedCategory)}&`;
            if (selectedTag) url += `tag=${encodeURIComponent(selectedTag)}&`;
            if (selectedSort) url += `sort=${encodeURIComponent(selectedSort)}&`;
            const res = await api.get(url);
            setPosts(res.data || []);
        } catch (error) {
            console.error('Failed to load posts:', error);
            toast.error('Failed to load forum posts.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col relative overflow-hidden bg-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {/* Premium Header Background */}
            <div className="absolute top-0 left-0 right-0 h-[450px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white pointer-events-none z-0" />
            <NavigationHeader />

            {/* Standard Breadcrumb Row (Blog Style) */}
            <div className="pt-16 pb-2 px-6 max-w-7xl mx-auto w-full relative z-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-50 pb-6 mb-4">
                    <div className="flex items-center gap-2 text-sm text-slate-500 font-medium">
                        <Link to="/dashboard" className="hover:text-slate-900 transition-colors">Dashboard</Link>
                        <Icon name="ChevronRight" size={16} />
                        <span className="text-slate-900">Community</span>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="relative group w-60 hidden sm:block">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                <Search size={14} className="text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                            </div>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search experiences..."
                                className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/5 transition-all outline-none placeholder:text-slate-400"
                            />
                        </div>
                        <Link
                            to="/community/create"
                            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 text-white text-xs font-boldtracking-wider rounded-lg hover:bg-emerald-600 transition-all shadow-sm active:scale-95 whitespace-nowrap"
                        >
                            <Plus size={14} />
                            Write Post
                        </Link>
                    </div>
                </div>
            </div>

            <main className="flex-1 max-w-7xl mx-auto w-full px-6 lg:px-8 pt-4 pb-12 relative z-10">

                <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 scrollbar-none">
                    {[
                        { id: 'latest', name: 'Newest' },
                        { id: 'trending', name: 'Trending' },
                        { id: 'top', name: 'Top Rated' }
                    ].map(sort => (
                        <button
                            key={sort.id}
                            onClick={() => setSelectedSort(sort.id)}
                            className={`px-6 py-2 rounded-lg text-[11px] font-boldtracking-wider transition-all whitespace-nowrap border ${selectedSort === sort.id
                                    ? 'bg-emerald-500 text-white border-emerald-500'
                                    : 'bg-white text-slate-500 border-slate-200 hover:border-emerald-500 hover:text-emerald-600'
                                }`}
                        >
                            {sort.name}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                    {/* Left Column: Feed */}
                    <div className="lg:col-span-2">

                        {loading ? (
                            <div className="flex justify-center p-20">
                                <Icon name="Loader2" className="animate-spin text-emerald-600" size={32} />
                            </div>
                        ) : posts.length === 0 ? (
                            <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-slate-100">
                                <Icon name="MessageSquare" size={48} className="mx-auto text-slate-300 mb-4" />
                                <p className="text-lg font-normal text-slate-500">No experiences yet</p>
                                <p className="text-slate-500 mt-1 max-w-xs mx-auto">Be the first to share your journey with the community!</p>
                            </div>
                        ) : (
                            posts.map((post) => (
                                <Link
                                    key={post.id}
                                    to={`/community/post/${post.id}`}
                                    className="block p-5 bg-white rounded-2xl border border-slate-100 transition-all group relative mb-6"
                                >
                                    <div className="flex items-center justify-between mb-5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-[10px] font-bold overflow-hidden">
                                                {post.author_avatar ? (
                                                    <img src={post.author_avatar} alt={post.author_name} className="w-full h-full object-cover" />
                                                ) : (
                                                    post.author_name.charAt(0)
                                                )}
                                            </div>
                                            <div>
                                                <h4 className="text-xs font-semibold text-gray-800 leading-none">{post.author_name}</h4>
                                                <p className="text-[10px] text-gray-400 mt-1">
                                                    {new Date(post.created_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[9px] font-bold rounded-full border border-emerald-100 tracking-tight uppercase">
                                                Verified
                                            </span>
                                        </div>
                                    </div>

                                    <h2 className="text-base font-bold text-gray-900 leading-snug group-hover:text-emerald-600 transition-colors mb-2 line-clamp-2">
                                        {post.title}
                                    </h2>
                                    <p className="text-[13px] text-gray-500 leading-relaxed line-clamp-2 mb-4">
                                        {post.content.replace(/<[^>]+>/g, ' ')}
                                    </p>

                                    <div className="flex flex-wrap items-center gap-2 mb-6">
                                        {post.tags?.map((tag) => (
                                            <span key={tag} className="text-[10px] font-boldtracking-[0.15em] text-emerald-600">
                                                #{tag}
                                            </span>
                                        ))}
                                    </div>

                                    <div className="flex items-center justify-between pt-5 border-t border-slate-50 text-gray-400 text-xs">
                                        <div className="flex items-center gap-6">
                                            <div className="flex items-center gap-2 group-hover:text-emerald-600 transition-colors">
                                                <Icon name="ThumbsUp" size={14} className="text-emerald-500" />
                                                <span className="font-medium">{post.upvotes || 0}</span>
                                            </div>
                                            <div className="flex items-center gap-2 group-hover:text-emerald-600 transition-colors">
                                                <Icon name="MessageSquare" size={14} />
                                                <span className="font-medium">{post.comment_count || 0}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Icon name="Eye" size={14} className="text-emerald-500" />
                                            <span className="font-medium">{post.views || 0}</span>
                                        </div>
                                    </div>
                                </Link>
                            ))
                        )}
                    </div>

                    {/* Right Column: Sidebars */}
                    <aside className="space-y-6">
                        {/* Popular Tags - Premium Light */}
                        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                            <div className="flex items-center justify-between mb-5">
                                <h3 className="text-[10px] font-blacktracking-[0.2em] text-slate-400">Popular Tags</h3>
                                <Icon name="Hash" size={14} className="text-slate-300" />
                            </div>
                            <div className="space-y-4">
                                {trendingTopics.length > 0 ? (
                                    trendingTopics.map((topic) => (
                                        <div
                                            key={topic.tag}
                                            onClick={() => {
                                                setSelectedTag(topic.tag);
                                                setSelectedCategory('');
                                            }}
                                            className="flex items-center justify-between group cursor-pointer"
                                        >
                                            <span className="text-xs font-bold text-slate-600 group-hover:text-emerald-600 transition-all leading-none flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-slate-200 group-hover:bg-emerald-500 transition-colors" />
                                                #{topic.tag}
                                            </span>
                                            <span className="text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-100 px-2.5 py-1 rounded-md group-hover:text-emerald-600 group-hover:bg-emerald-50 transition-all">{topic.count}</span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-[10px] text-slate-400 italic">No trending topics yet</p>
                                )}
                            </div>
                        </div>

                        {/* Similar Discussions */}

                    </aside>
                </div>
            </main>
        </div>
    );
};

export default CommunityForum;
